import { useMemo, useState } from "react";
import useSeo from "@/hooks/useSeo";
import MatchCard from "@/components/feature/MatchCard";
import { matches } from "@/mocks/matches";
import { formatDateCN, todayStr, uniqueDates } from "@/utils";

export default function Schedule() {
  useSeo({
    title: "NBA直播吧 - NBA赛程列表",
    description:
      "NBA赛程列表页，提供完整NBA比赛日程，可按日期查询对阵双方、比赛时间、比赛地点与赛果简要，提前了解即将进行的NBA比赛。",
    keywords: "NBA赛程,NBA赛程表,NBA比赛时间,NBA对阵,NBA比赛地点",
  });

  const dates = uniqueDates(matches.map((m) => m.date));
  const today = todayStr();
  const [selected, setSelected] = useState(dates.includes(today) ? today : dates[0]);
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const list = useMemo(() => {
    const arr = matches.filter((m) => m.date === selected);
    arr.sort((a, b) => (order === "asc" ? a.time.localeCompare(b.time) : b.time.localeCompare(a.time)));
    return arr;
  }, [selected, order]);

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-foreground-400 font-label mb-2">
          <i className="ri-home-4-line"></i> 首页 <i className="ri-arrow-right-s-line"></i> <span className="text-foreground-600">NBA赛程</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">NBA赛程</h1>
            <p className="text-sm text-foreground-500 mt-1">按日期查询NBA比赛安排，点击任意比赛查看详情</p>
          </div>
          <div className="inline-flex items-center gap-1 bg-background-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => setOrder("asc")}
              className={`px-4 py-1.5 rounded-full text-xs font-label cursor-pointer transition-colors ${
                order === "asc" ? "bg-secondary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              时间升序
            </button>
            <button
              type="button"
              onClick={() => setOrder("desc")}
              className={`px-4 py-1.5 rounded-full text-xs font-label cursor-pointer transition-colors ${
                order === "desc" ? "bg-secondary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              时间降序
            </button>
          </div>
        </div>
      </div>

      <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 mb-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
            <i className="ri-calendar-line text-primary-500"></i> 日期导航
          </span>
          <label className="inline-flex items-center gap-2 text-xs text-foreground-500 cursor-pointer">
            <i className="ri-calendar-schedule-line"></i>
            <input
              type="date"
              value={selected}
              onChange={(e) => e.target.value && setSelected(e.target.value)}
              className="bg-background-100 border border-background-300 rounded-md px-2.5 py-1.5 text-xs text-foreground-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {dates.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setSelected(d)}
              className={`flex flex-col items-center px-4 py-2.5 rounded-lg border whitespace-nowrap transition-colors cursor-pointer ${
                selected === d
                  ? "bg-primary-500 border-primary-500 text-background-50"
                  : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              <span className="text-xs font-label">{d.slice(5).replace("-", "/")}</span>
              <span className="text-[11px] mt-0.5">{formatDateCN(d).split(" ")[1]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
          {formatDateCN(selected)} 赛程
        </h2>
        <span className="text-xs text-foreground-500 font-label">共 {list.length} 场比赛</span>
      </div>

      {list.length === 0 ? (
        <div className="bg-background-50 rounded-lg border border-background-200 py-20 flex flex-col items-center gap-3 text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-calendar-close-line"></i>
          </span>
          <p className="text-foreground-900 font-medium">暂无赛程安排</p>
          <p className="text-sm text-foreground-500">该日期暂无NBA比赛，请选择其他日期查看</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-product-shop>
          {list.map((m) => (
            <MatchCard key={m.id} match={m} variant="row" />
          ))}
        </div>
      )}
    </div>
  );
}